(() => {
  if (customElements.get("cinematic-slider-portable")) return;

  const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "textarea:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");

  const escapeSelectorValue = (value) => {
    if (window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(value);
    }
    return String(value).replace(/["\\]/g, "\\$&");
  };

  const getCartAddUrl = () => window.routes?.cart_add_url || "/cart/add.js";

  const getFetchConfig = () => {
    if (typeof window.fetchConfig === "function") {
      const config = window.fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      return config;
    }

    return {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    };
  };

  class CinematicSliderPortable extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.items = [];
      this.videos = [];
      this.videoMap = new Map();
      this.enableVideoAutoplay = true;
      this.isSnapping = false;
      this.snapTimeout = null;
      this.rafId = null;
      this.currentOpener = null;
      this.drag = {
        active: false,
        pointerId: null,
        startX: 0,
        startScrollLeft: 0,
        moved: false,
      };

      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
    }

    connectedCallback() {
      if (this.hasAttribute("data-cinematic-ready")) return;
      this.setAttribute("data-cinematic-ready", "true");
      if (!this.hasAttribute("dir")) this.setAttribute("dir", "ltr");

      this.track = this.querySelector("[data-cinematic-track]");
      this.prevButton = this.querySelector("[data-cinematic-prev]");
      this.nextButton = this.querySelector("[data-cinematic-next]");
      this.items = Array.from(this.querySelectorAll("[data-cinematic-item]"));
      this.videos = Array.from(this.querySelectorAll("[data-cinematic-video]"));
      this.enableVideoAutoplay = this.dataset.enableVideoAutoplay === "true";
      this.videoMap = new Map();

      this.videos.forEach((video) => {
        const index = Number(video.dataset.cinematicVideoIndex);
        if (!Number.isNaN(index)) this.videoMap.set(index, video);
      });

      if (!this.track || !this.items.length) return;

      this.cacheItems();
      this.bindEvents();

      this.inView = false;
      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.inView = entry.isIntersecting;
          if (this.inView) {
            this.setActive(this.activeIndex);
          } else {
            this.videos.forEach((video) => this.pauseVideo(video));
          }
        });
      }, { rootMargin: "200px 0px", threshold: 0.01 });
      this.visibilityObserver.observe(this);

      requestAnimationFrame(() => {
        this.updateActive();
        this.scrollToIndex(this.activeIndex, false);
        this.setActive(this.activeIndex);
      });
    }

    disconnectedCallback() {
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      this.removeEventListener("click", this.onClick);
      document.removeEventListener("keydown", this.onKeyDown, true);
      window.removeEventListener("resize", this.onResize);
      this.track?.removeEventListener("scroll", this.onScroll);
      this.track?.removeEventListener("pointerdown", this.onPointerDown);
      this.track?.removeEventListener("pointermove", this.onPointerMove);
      this.track?.removeEventListener("pointerup", this.onPointerUp);
      this.track?.removeEventListener("pointercancel", this.onPointerUp);
      clearTimeout(this.snapTimeout);
      cancelAnimationFrame(this.rafId);
      this.removeAttribute("data-cinematic-ready");
    }

    bindEvents() {
      this.addEventListener("click", this.onClick);
      document.addEventListener("keydown", this.onKeyDown, true);
      window.addEventListener("resize", this.onResize, { passive: true });
      this.track.addEventListener("scroll", this.onScroll, { passive: true });
      this.track.addEventListener("pointerdown", this.onPointerDown);
      this.track.addEventListener("pointermove", this.onPointerMove);
      this.track.addEventListener("pointerup", this.onPointerUp);
      this.track.addEventListener("pointercancel", this.onPointerUp);
    }

    cacheItems() {
      this.itemData = this.items.map((item) => ({
        el: item,
        center: item.offsetLeft + item.offsetWidth / 2,
      }));
    }

    onResize() {
      this.cacheItems();
      this.requestActiveUpdate();
    }

    onScroll() {
      this.requestActiveUpdate();
    }

    requestActiveUpdate() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        if (!this.isSnapping) this.updateActive();
        this.rafId = null;
      });
    }

    updateActive() {
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const isAtEnd = this.track.scrollLeft >= maxScroll - 2;

      if (isAtEnd) {
        this.setActive(this.items.length - 1);
        return;
      }

      let closest = 0;
      let minDistance = Infinity;

      this.itemData.forEach((item, index) => {
        const distance = Math.abs(item.center - center);
        if (distance < minDistance) {
          minDistance = distance;
          closest = index;
        }
      });

      this.setActive(closest);
    }

    setActive(index) {
      index = Math.max(0, Math.min(index, this.items.length - 1));
      if (index !== this.activeIndex) this.closeAllPopovers();

      this.items.forEach((item, itemIndex) => {
        const isActive = itemIndex === index;
        item.setAttribute("data-active", isActive ? "true" : "false");
        item.setAttribute("aria-current", isActive ? "true" : "false");
        item.setAttribute("tabindex", isActive ? "0" : "-1");

        const toggle = item.querySelector("[data-cinematic-toggle]");
        if (toggle) {
          toggle.disabled = false;
          toggle.setAttribute("aria-disabled", "false");
        }

        const playButton = item.querySelector("[data-cinematic-play]");
        if (playButton) {
          playButton.disabled = !isActive;
          playButton.setAttribute("aria-disabled", isActive ? "false" : "true");
          playButton.hidden = !isActive;
        }
      });

      this.activeIndex = index;

      if (this.enableVideoAutoplay) {
        this.videos.forEach((video) => {
          const videoIndex = Number(video.dataset.cinematicVideoIndex);
          if (videoIndex === index) {
            this.playVideo(video);
          } else {
            this.pauseVideo(video, true);
          }
        });
      } else {
        this.videos.forEach((video) => this.pauseVideo(video, true));
      }

      this.syncMuteButtons();
      this.updateButtons();
    }

    updateButtons() {
      if (this.prevButton) this.prevButton.disabled = this.activeIndex <= 0;
      if (this.nextButton) this.nextButton.disabled = this.activeIndex >= this.items.length - 1;
    }

    goTo(index) {
      index = Math.max(0, Math.min(index, this.items.length - 1));
      this.isSnapping = true;
      this.scrollToIndex(index, true);
    }

    scrollToIndex(index, smooth = true) {
      const item = this.itemData[index];
      if (!item) return;

      const offset = item.el.offsetLeft - (this.track.clientWidth - item.el.offsetWidth) / 2;
      if (smooth) this.track.style.scrollSnapType = "none";
      this.setActive(index);
      this.track.scrollTo({ left: offset, behavior: smooth ? "smooth" : "auto" });

      clearTimeout(this.snapTimeout);
      this.snapTimeout = setTimeout(() => {
        this.isSnapping = false;
        this.track.style.scrollSnapType = "";
        this.updateActive();
      }, smooth ? 380 : 0);
    }

    onPointerDown(event) {
      if (event.button !== 0) return;
      if (event.target.closest(FOCUSABLE_SELECTOR)) return;
      this.drag.active = true;
      this.drag.pointerId = event.pointerId;
      this.drag.startX = event.clientX;
      this.drag.startScrollLeft = this.track.scrollLeft;
      this.drag.moved = false;
      this.track.classList.add("is-dragging");
      this.track.setPointerCapture?.(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.drag.active || event.pointerId !== this.drag.pointerId) return;
      const delta = event.clientX - this.drag.startX;
      if (Math.abs(delta) > 5) this.drag.moved = true;
      this.track.scrollLeft = this.drag.startScrollLeft - delta;
    }

    onPointerUp(event) {
      if (!this.drag.active || event.pointerId !== this.drag.pointerId) return;
      this.drag.active = false;
      this.track.classList.remove("is-dragging");
      this.track.releasePointerCapture?.(event.pointerId);
      if (this.drag.moved) {
        window.setTimeout(() => this.scrollToIndex(this.activeIndex, true), 20);
      }
    }

    onClick(event) {
      if (this.drag.moved) {
        this.drag.moved = false;
        return;
      }

      const prev = event.target.closest("[data-cinematic-prev]");
      if (prev) {
        event.preventDefault();
        this.goTo(this.activeIndex - 1);
        return;
      }

      const next = event.target.closest("[data-cinematic-next]");
      if (next) {
        event.preventDefault();
        this.goTo(this.activeIndex + 1);
        return;
      }

      const play = event.target.closest("[data-cinematic-play]");
      if (play) {
        event.preventDefault();
        this.toggleVideo(play);
        return;
      }

      const mute = event.target.closest("[data-cinematic-mute]");
      if (mute) {
        event.preventDefault();
        this.toggleMute(mute);
        return;
      }

      const toggle = event.target.closest("[data-cinematic-toggle]");
      if (toggle) {
        event.preventDefault();
        const item = toggle.closest("[data-cinematic-item]");
        const index = Number(item?.dataset.index);

        if (!Number.isNaN(index) && index !== this.activeIndex) {
          this.goTo(index);
          window.setTimeout(() => this.openPopover(toggle), 220);
          return;
        }

        this.togglePopover(toggle);
        return;
      }

      const close = event.target.closest("[data-cinematic-close]");
      if (close) {
        event.preventDefault();
        this.closePopover(this.getOpenerById(close.dataset.cinematicClose));
        return;
      }

      const add = event.target.closest("[data-cinematic-add]");
      if (add) {
        event.preventDefault();
        this.handleAddToCart(add);
      }
    }

    onKeyDown(event) {
      if (event.key === "Escape" && this.currentOpener) {
        event.preventDefault();
        this.closePopover(this.currentOpener);
        return;
      }

      if (!this.contains(document.activeElement)) return;
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable) return;

      event.preventDefault();
      this.goTo(event.key === "ArrowLeft" ? this.activeIndex - 1 : this.activeIndex + 1);
      this.items[this.activeIndex]?.focus({ preventScroll: true });
    }

    playVideo(video) {
      if (!video) return;
      if (this.inView === false) return;
      const promise = video.play();
      if (promise?.catch) promise.catch(() => {});
      video.closest("[data-cinematic-item]")?.querySelector("[data-cinematic-play]")?.setAttribute("aria-pressed", "true");
    }

    pauseVideo(video, reset = false) {
      if (!video) return;
      video.pause();
      if (reset && video.readyState > 0) {
        try {
          video.currentTime = 0;
        } catch (error) {}
      }
      video.closest("[data-cinematic-item]")?.querySelector("[data-cinematic-play]")?.setAttribute("aria-pressed", "false");
      video.muted = true;
    }

    toggleVideo(button) {
      const item = button.closest("[data-cinematic-item]");
      const video = item?.querySelector("[data-cinematic-video]");
      if (!video) return;

      if (video.paused || video.ended) {
        this.playVideo(video);
      } else {
        this.pauseVideo(video, false);
      }

      this.syncMuteButtons();
    }

    toggleMute(button) {
      const item = button.closest("[data-cinematic-item]");
      const video = item?.querySelector("[data-cinematic-video]");
      if (!video) return;
      video.muted = !video.muted;
      this.updateMuteButton(button, video);
    }

    syncMuteButtons() {
      this.items.forEach((item, index) => {
        const button = item.querySelector("[data-cinematic-mute]");
        const video = item.querySelector("[data-cinematic-video]");
        if (!button || !video) return;
        const isPlaying = !video.paused && !video.ended;
        const shouldShow = this.enableVideoAutoplay ? index === this.activeIndex : isPlaying;
        button.classList.toggle("is-visible", shouldShow);
        this.updateMuteButton(button, video);
      });
    }

    updateMuteButton(button, video) {
      button.setAttribute("aria-label", video.muted ? "Muted" : "Unmuted");
      button.setAttribute("aria-pressed", video.muted ? "true" : "false");
    }

    getOpenerById(id) {
      if (!id) return null;
      return this.querySelector(`[data-cinematic-toggle="${escapeSelectorValue(id)}"]`);
    }

    openPopover(opener) {
      if (!opener) return;
      const id = opener.dataset.cinematicToggle;
      const popover = this.querySelector(`[data-cinematic-popover="${escapeSelectorValue(id)}"]`);
      if (!popover) return;

      this.closeAllPopovers();
      popover.hidden = false;
      opener.setAttribute("aria-expanded", "true");
      this.currentOpener = opener;
      this.focusFirst(popover);
    }

    closePopover(opener, returnFocus = true) {
      if (!opener) return;
      const id = opener.dataset.cinematicToggle;
      const popover = this.querySelector(`[data-cinematic-popover="${escapeSelectorValue(id)}"]`);
      if (!popover) return;

      popover.hidden = true;
      opener.setAttribute("aria-expanded", "false");
      if (this.currentOpener === opener) this.currentOpener = null;
      if (returnFocus) opener.focus({ preventScroll: true });
    }

    togglePopover(opener) {
      const id = opener.dataset.cinematicToggle;
      const popover = this.querySelector(`[data-cinematic-popover="${escapeSelectorValue(id)}"]`);
      if (!popover) return;
      if (popover.hidden) {
        this.openPopover(opener);
      } else {
        this.closePopover(opener);
      }
    }

    closeAllPopovers() {
      this.querySelectorAll("[data-cinematic-popover]").forEach((popover) => {
        popover.hidden = true;
      });
      this.querySelectorAll("[data-cinematic-toggle]").forEach((toggle) => {
        toggle.setAttribute("aria-expanded", "false");
      });
      this.currentOpener = null;
    }

    focusFirst(container) {
      const focusable = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
        return !element.hasAttribute("disabled") && element.offsetParent !== null;
      });
      focusable[0]?.focus({ preventScroll: true });
    }

    async handleAddToCart(button) {
      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
      const idle = button.querySelector("[data-cinematic-add-idle]");
      const previousText = idle?.textContent;

      button.classList.add("is-loading");
      button.setAttribute("aria-busy", "true");
      button.disabled = true;
      button.setAttribute("aria-disabled", "true");

      try {
        const config = getFetchConfig();
        const formData = new FormData();
        formData.append("id", variantId);
        formData.append("quantity", "1");

        if (cart && typeof cart.getSectionsToRender === "function") {
          formData.append(
            "sections",
            cart
              .getSectionsToRender()
              .map((section) => section.id)
              .join(","),
          );
          formData.append("sections_url", window.location.pathname);
          if (typeof cart.setActiveElement === "function") {
            cart.setActiveElement(button);
          }
        }

        config.body = formData;

        const response = await fetch(getCartAddUrl(), config);

        const data = await response.json();

        if (data.status) {
          if (typeof window.publish === "function" && typeof window.PUB_SUB_EVENTS !== "undefined") {
            window.publish(window.PUB_SUB_EVENTS.cartError, {
              source: "cinematic-slider-portable",
              productVariantId: variantId,
              errors: data.errors || data.description,
              message: data.message,
            });
          }
          throw data;
        }

        if (idle) idle.textContent = "Added";
        this.closeAllPopovers();

        if (typeof window.publish === "function" && typeof window.PUB_SUB_EVENTS !== "undefined") {
          try {
            await window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: "cinematic-slider-portable",
              productVariantId: variantId,
              cartData: data,
            });
          } catch (error) {}
        }

        if (cart && typeof cart.renderContents === "function") {
          cart.renderContents(data);
        } else {
          await this.refreshCartCount();
          const openedCartUi = await this.openCartUi(button);
          if (!openedCartUi) this.showFallbackCartConfirmation(button, data);
        }

        window.setTimeout(() => {
          if (idle && previousText) idle.textContent = previousText;
        }, 1600);
      } catch (error) {
        document.dispatchEvent(new CustomEvent("cart-error", { detail: { error } }));
        console.error("Cinematic slider add to cart error:", error);
      } finally {
        button.classList.remove("is-loading");
        button.setAttribute("aria-busy", "false");
        button.disabled = false;
        button.setAttribute("aria-disabled", "false");
      }
    }

    getCartSections() {
      const sections = [];
      const drawer =
        document.getElementById("Cart-Drawer") ||
        document.querySelector("cart-drawer");
      const bubble =
        document.getElementById("Cart-Icon-Bubble") ||
        document.getElementById("cart-icon-bubble");

      if (bubble?.id) {
        sections.push({
          id: bubble.id,
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        });
      }

      if (drawer?.id) {
        sections.push({
          id: drawer.id,
          section: drawer.dataset?.sectionId || "cart-drawer",
          selector: "[data-cart-drawer-content]",
        });
      }

      return sections;
    }

    renderCartSections(sections = {}) {
      this.getCartSections().forEach((section) => {
        const html = sections[section.section];
        if (!html) return;

        const container = document.getElementById(section.id);
        if (!container) return;

        const parsed = new DOMParser().parseFromString(html, "text/html");
        const element = section.selector
          ? parsed.querySelector(section.selector)
          : parsed.body.firstElementChild;

        if (!element) return;

        if (section.selector === "[data-cart-drawer-content]") {
          const contentTarget = container.querySelector("[data-cart-drawer-content]");
          const summaryTarget = container.querySelector("[data-cart-drawer-summary]");
          const summarySource = parsed.querySelector("[data-cart-drawer-summary]");

          if (contentTarget) contentTarget.innerHTML = element.innerHTML;
          if (summaryTarget && summarySource) {
            summaryTarget.innerHTML = summarySource.innerHTML;
            summaryTarget.hidden = false;
            summaryTarget.classList.remove("hidden");
          }
        } else {
          container.innerHTML = element.innerHTML;
        }
      });
    }

    async refreshCartCount() {
      const targets = document.querySelectorAll(
        "[data-cart-count], .cart-count-bubble span:first-child, #cart-icon-bubble span:first-child",
      );
      if (!targets.length) return;

      try {
        const response = await fetch("/cart.js", {
          headers: { Accept: "application/json" },
        });
        const cart = await response.json();
        targets.forEach((target) => {
          target.textContent = cart.item_count;
        });
      } catch (error) {}
    }

    async openCartUi(opener) {
      const drawer =
        document.getElementById("Cart-Drawer") ||
        document.querySelector("cart-drawer");
      const notification = document.querySelector("cart-notification");

      if (drawer) {
        if (typeof drawer.show === "function") {
          drawer.show(opener);
          return true;
        }

        if (typeof drawer.open === "function") {
          drawer.open(opener);
          return true;
        }

        drawer.setAttribute("open", "");
        drawer.classList.add("is-active", "active");
        return true;
      }

      if (notification && typeof notification.open === "function") {
        notification.open();
        return true;
      }

      return false;
    }

    showFallbackCartConfirmation(button, cartData) {
      const item = button.closest("[data-cinematic-item]");
      if (!item) return;

      item.querySelector("[data-cinematic-fallback-cart]")?.remove();

      const confirmation = document.createElement("div");
      confirmation.className = "cinematic-slider__fallback-cart";
      confirmation.setAttribute("data-cinematic-fallback-cart", "");
      confirmation.setAttribute("role", "status");
      confirmation.innerHTML = `
        <span class="cinematic-slider__fallback-cart-title">Producto agregado</span>
        <a class="cinematic-slider__fallback-cart-link" href="/cart">Ver carrito</a>
      `;

      item.appendChild(confirmation);

      window.setTimeout(() => {
        confirmation.remove();
      }, 3200);
    }
  }

  customElements.define("cinematic-slider-portable", CinematicSliderPortable);
})();
